// dependencies
import { useMutation, useQuery } from "@tanstack/react-query";

import {
	ISelectionObject,
	applyBulkGoalArchiveInternalApi,
	applyBulkUpdate,
	fetchBulkSelections,
	fetchNewBulkSelectionObjectID,
	mutateItemsInBulkSelection,
} from "../../services";

export const useBulkSelectionObject = (selectionID: string) => {
	return useQuery({
		queryKey: ["bulk-selections", selectionID],
		queryFn: () => fetchBulkSelections(selectionID),
		meta: {
			errorMessage: "There was an error getting the bulk selection object.",
		},
	});
};

export type BulkSelectionQuery = {
	field: string;
	operator: string;
	value: any;
};

declare interface BaseBulkSelectionMutation {
	action: "add" | "remove";
}

export interface BulkSelectionItemMutation extends BaseBulkSelectionMutation {
	object_ids?: string[];
}

interface BulkSelectionQueryMutation extends BaseBulkSelectionMutation {
	query?: BulkSelectionQuery[];
}

export type BulkSelectionMutation = BulkSelectionItemMutation | BulkSelectionQueryMutation;

export const useBulkSelectionMutation = (
	selectionID: string,
	onError: (_error, _rollbackObject: BulkSelectionMutation) => void = () => {},
	onSuccess = () => {}
) => {
	return useMutation({
		mutationKey: ["bulk-selections", selectionID],
		mutationFn: (bulkSelectionMutationObject: BulkSelectionMutation) =>
			mutateItemsInBulkSelection(selectionID, bulkSelectionMutationObject),
		onMutate: (bulkSelectionMutationObject: BulkSelectionMutation) => bulkSelectionMutationObject,
		onError,
		onSuccess,
	});
};

export type BulkSelectionObjectCreationObject = {
	object_ids?: string[];
	query?: BulkSelectionQuery[];
};

export const useBulkSelectionObjectCreationMutation = (
	type: string,
	onSuccess: (
		_data: ISelectionObject,
		bulkSelectionObjectCreationObject: BulkSelectionObjectCreationObject
	) => void = () => {},
	onError = () => {}
) => {
	return useMutation({
		mutationKey: ["bulk-selections-object-create", type],
		mutationFn: (bulkSelectionObjectCreationObject: BulkSelectionObjectCreationObject) =>
			fetchNewBulkSelectionObjectID(type, bulkSelectionObjectCreationObject),
		onSuccess,
		onError,
	});
};

export const useApplyBulkUpdateMutation = (
	selectionID: string,
	type: string,
	onSuccess = () => {},
	onError = () => {},
	shouldUseInternalApiForArchirve: boolean = false // Emphemeral flag to use internal api for archive
) => {
	return useMutation({
		mutationKey: ["bulk-selections-apply", selectionID],
		mutationFn: (bulkUpdateObject) => {
			const shouldUseInternalApi =
				shouldUseInternalApiForArchirve && type === "goal" && bulkUpdateObject["status"] === "archived";

			if (shouldUseInternalApi) {
				return applyBulkGoalArchiveInternalApi(selectionID, bulkUpdateObject);
			}
			return applyBulkUpdate(selectionID, type, bulkUpdateObject);
		},
		onSuccess,
		onError,
	});
};
